import { FormTypes } from "../../../utils/models";
import { Paper, Theme } from "@mui/material";
import { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import AboutHelpModal from "./AboutHelpModal";
import { Alert } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import CustomStepper from "../../../components/CustomStepper";
import FormLayout from "../../../components/FormLayout";
import InfoCircleIcon from "@mui/icons-material/InfoRounded";
import LineLoader from "../../../components/Loaders/LineLoader/LineLoader";
import { toDollar } from "../../../utils/dataConverter";
import ConfirmW2 from "./ConfirmW2";
import { taxSlice } from "../../../store/reducer/tax";
import { goBack } from "redux-first-history";
import {
  ConfirmW2Task,
  DeleteW2Task,
  selectTask,
  UploadW2Task,
} from "../../../utils/tasks";
import { RootState } from "../../../store";
import { TaxDocument, TaxDocumentError } from "../../../model/tax/tax_document";
import { IRSW2Form, validateIRSW2Form } from "../../../model/tax/irsw2.d";
import FileDropContainer from "../../../components/FileInputWithDragAndDrop/FileDropContainer";
import { IRS1040Form } from "../../../model/tax/irs1040";
import { User } from "../../../model/user";
import { FileRejection } from "react-dropzone";
import PromptLocalWithholdings from "./PromptLocalWithholdings";
import PromptLocalWithholdingsUponUpload from "./PromptLocalWithholdingsUponUpload";
import DocumentList from "./DocumentList";
import { ErrorAlert } from "../../../components/Alerts";
import PromptUnusualW2 from "./PromptUnusualW2";
import PromptMissingBox18 from "./PromptMissingBox18";

const steps = ["upload w2", "confirm"];
interface FormsW2Props {
  taxState: RootState["tax"];
  user: User | null;
}

const FormsW2 = ({ taxState, user }: FormsW2Props) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [fileDropError, setFileDropError] = useState("");
  const onDropRejected = (rejectedFiles: FileRejection[]) => {
    setFileDropError("Upload failed: " + rejectedFiles[0].errors[0].message);
    setTimeout(() => setFileDropError(""), 4000);
  };

  const totalW2 = taxState.irsW2Forms
    .filter((w2) => w2.document_name !== "error")
    .map((w2) =>
      Number(
        (w2 as TaxDocument<IRSW2Form>).form_data.wages_tips_other_comp_box1
      )
    )
    .reduce((acc, wages) => acc + wages, 0);

  const wages1040 =
    (taxState.irs1040 as TaxDocument<IRS1040Form>)?.form_data
      ?.wages_salary_tips ?? 0;

  const wagesMatch =
    Number(wages1040) + 700 > totalW2 && Number(wages1040) - 700 < totalW2;

  const uploadW2Task = selectTask(taxState, UploadW2Task.id);
  const confirmW2Task = selectTask(taxState, ConfirmW2Task.id);
  const deleteW2Task = selectTask(taxState, DeleteW2Task.id);

  const hasSpouse = user?.is_jointfiling ?? false;
  const userName = !user?.first_name
    ? undefined
    : user!.first_name + " " + (user!.last_name ?? "");
  const spouseName = !user?.spouse_first_name
    ? undefined
    : user!.spouse_first_name + " " + (user!.spouse_last_name ?? "");

  const lastW2 = taxState.irsW2Forms[taxState.irsW2Forms.length - 1];

  useEffect(() => {
    dispatch(taxSlice.actions.setEditMode({}));
    dispatch(taxSlice.actions.fetchW2FormDetails());
  }, [dispatch]);

  const handleNext = () => {
    if (taxState.irsW2Forms.length) {
      // Note that client requested that a local withholdings check and prompt
      // be added immediately upon OCR response. Because of that we won't check for empty
      // withholdings here (sending true). If client does want to check twice, then pass "false".
      // Although users will experience duplicate prompts for empty withholdings
      dispatch(taxSlice.actions.confirmAllW2Request({
        skip_missing_lwh: true,
        skip_missing_box18: false,
      }));
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const deleteFile = (index: number) => {
    updateEditMode(index)(false);
    dispatch(taxSlice.actions.deleteW2Request(index));
  };

  const onDropAccepted = (acceptedFiles: any) => {
    dispatch(taxSlice.actions.uploadW2Request(acceptedFiles[0] as File));
  };

  const updateEditMode = (index: number) => (value: boolean) => {
    dispatch(taxSlice.actions.toggleEditMode({ index, value }));
  };

  const isNextDisabled = () => {
    if (!taxState.irsW2Forms.length) {
      return !taxState.uploadedW2s?.length;
    }
    return (
      !wagesMatch ||
      taxState.irsW2Forms.some(
        (form) =>
          form.document_name === "error" || !validateIRSW2Form(form.form_data)
      )
    );
  };

  return (
    <>
      <CustomStepper
        steps={steps}
        activeStep={taxState.irsW2Forms.length ? 1 : 0}
        handleNext={handleNext}
        handleBack={() => dispatch(goBack())}
        className="upload-stepper"
        showBackButton={true}
        disableSteps={true}
        nextBtnDisable={isNextDisabled()}
      >
        {!taxState.uploadedW2s ? (
          <LineLoader />
        ) : (
          <FormLayout
            title={"W-2 Form(s)"}
            subtitle={"File upload"}
            description={
              <span>
                {`It looks like you have income from wages.
              We’ll need you to upload all\nthe W-2’s you received from your employer(s).
              Upload one W-2 at a time. If your W-2 has more than one page, upload all pages of that W-2 as a single file.`}
              </span>
            }
            className="upload-form-layout"
            icon={
              <ContactSupportIcon
                titleAccess="Help"
                className={"help-icon"}
                onClick={handleOpen}
              />
            }
          >
            <h5>Documents ({taxState.irsW2Forms.length})</h5>
            <div className="upload-column-wrapper">
              <DocumentList
                documents={taxState.irsW2Forms}
                formName="W-2"
                title={(document) =>
                  (document as TaxDocument<IRSW2Form>).form_data
                    .employer_name ?? document.uploaded_file.file_name
                }
                onDropAccepted={onDropAccepted}
                properties={[
                  {
                    name: "Wages & tips",
                    prop: "wages_tips_other_comp_box1" as keyof IRSW2Form,
                  },
                ]}
                showUpload={!wagesMatch}
              />
              <div className="activeDocument">
                {taxState.selectedFormIndex === -1 ? (
                  <FileDropContainer
                    uploadTask={uploadW2Task}
                    onDropAccepted={onDropAccepted}
                    onDropRejected={onDropRejected}
                  />
                ) : (
                  <ConfirmW2
                    index={taxState.selectedFormIndex}
                    editMode={true}
                    toggleEditMode={updateEditMode(taxState.selectedFormIndex)}
                    deleteFile={deleteFile}
                    hasSpouse={hasSpouse}
                    userName={userName}
                    spouseName={spouseName}
                    wagesMatch={wagesMatch}
                    w2={
                      taxState.irsW2Forms[
                        taxState.selectedFormIndex
                      ] as TaxDocument<IRSW2Form>
                    }
                  />
                )}
                <div className="mobileDocumentList">
                  {!wagesMatch && (
                    <FileDropContainer
                      uploadTask={uploadW2Task}
                      onDropAccepted={onDropAccepted}
                      onDropRejected={onDropRejected}
                    />
                  )}
                  {taxState.irsW2Forms.map(
                    (
                      fileInfo: TaxDocument<IRSW2Form> | TaxDocumentError,
                      index: number
                    ) =>
                      fileInfo.document_name === "error" ? (
                        <ErrorAlert>{fileInfo.message}</ErrorAlert>
                      ) : (
                        <ConfirmW2
                          index={index}
                          editMode={true}
                          hasSpouse={hasSpouse}
                          userName={userName}
                          spouseName={spouseName}
                          toggleEditMode={updateEditMode(
                            taxState.selectedFormIndex
                          )}
                          deleteFile={deleteFile}
                          w2={fileInfo}
                          wagesMatch={wagesMatch}
                        />
                      )
                  )}
                </div>
              </div>
            </div>

            {lastW2 && !uploadW2Task && (
              <Alert
                severity={
                  lastW2.document_name === "error" ? "error" : "success"
                }
                variant="outlined"
                classes={{
                  root:
                    lastW2.document_name === "error"
                      ? "error-alert floating-alert"
                      : "success-alert floating-alert",
                  message: "success-msg",
                  icon:
                    lastW2.document_name === "error"
                      ? "error-icon"
                      : "success-icon",
                }}
                iconMapping={{
                  success: <CheckCircleIcon />,
                  error: <InfoCircleIcon />,
                }}
              >
                {!(lastW2.document_name === "error")
                  ? "Your upload was successful."
                  : "Your upload failed, please try again."}
              </Alert>
            )}
            {fileDropError && <ErrorAlert floating>{fileDropError}</ErrorAlert>}
            {taxState.tax_return?.IRSW2.messages?.map((message) => (
              <ErrorAlert>{message}</ErrorAlert>
            ))}
            <Paper elevation={0} className="totalContainer">
              <div className="flex-column">
                <span className="form-static-title">
                  Total W-2 wages and tips:
                </span>
                <span
                  className={
                    "form-static-value " + (wagesMatch ? "w-match" : "w-fail")
                  }
                >
                  {toDollar(totalW2)}
                </span>
              </div>
              <div className="flex-column">
                <span className="form-static-title">
                  Wages and tips from Form 1040:
                </span>
                <span className="form-static-value">
                  {toDollar(wages1040)}
                </span>
              </div>
              <div className={wagesMatch ? "w-match" : "w-fail"}>
                {wagesMatch ? (
                  <span>
                    W-2 wages match Form 1040. Review <u>all data fields</u> on
                    each uploaded W-2 to make sure they match your file copy and
                    make any necessary edits before selecting “continue.”
                  </span>
                ) : (
                  <span>
                    W-2 wages must equal wages from Form 1040. Please ensure all
                    fields are correct or{" "}
                    <a
                      href="#"
                      onClick={() => {
                        dispatch(taxSlice.actions.selectForm(-1));
                        if (taxState.selectedFormIndex === -1) {
                          const input = document.createElement("input");
                          input.type = "file";
                          input.onchange = (e) => {
                            const files = (e.target as HTMLInputElement).files;
                            if (files) onDropAccepted(files);
                          };
                          input.click();
                        }
                      }}
                    >
                      upload additional W-2s.
                    </a>
                  </span>
                )}
              </div>
            </Paper>
          </FormLayout>
        )}
      </CustomStepper>

      <AboutHelpModal
        open={open}
        formType={FormTypes.w2}
        handleClose={handleClose}
      />
      {/* Prompt occurs immediately after upload */}
      <PromptLocalWithholdingsUponUpload
        show={taxState.showLocalWithholdingsUploadPrompt}
        onConfirm={() => {
          dispatch(
            taxSlice.actions.toggleShowLocalWithholdingsUploadPrompt(false)
          );
        }}
        onCancel={() => {
          dispatch(
            taxSlice.actions.toggleShowLocalWithholdingsUploadPrompt(false)
          );
          dispatch(
            taxSlice.actions.addLocalTax(taxState.irsW2Forms.length - 1)
          );
        }}
      />
      <PromptMissingBox18
        show={taxState.showMissingBox18Prompt}
        onContinue={() => {
          dispatch(taxSlice.actions.toggleShowMissingBox18Prompt(false));
          dispatch(taxSlice.actions.confirmAllW2Request({
            skip_missing_lwh: true,
            skip_missing_box18: true,
          }));
        }}
        onCancel={() => {
          dispatch(taxSlice.actions.toggleShowMissingBox18Prompt(false));
        }}
        />
      {/* Prompt occurs when hitting Continue (but show state currently not enabled due to adding similar prompt above) */}
      <PromptLocalWithholdings
        show={taxState.showLocalWithholdingsPrompt}
        onConfirm={() => {
          dispatch(taxSlice.actions.confirmAllW2Request({
            skip_missing_lwh: true,
            skip_missing_box18: false,
          }));
        }}
        onCancel={() => {
          dispatch(taxSlice.actions.toggleShowLocalWithholdingsPrompt(false));
        }}
      />
      <PromptUnusualW2
        show={taxState.showInvalidBox5Prompt || taxState.showInvalidLocalityPrompt}
        onConfirm={() => {
          dispatch(taxSlice.actions.toggleShowInvalidBox5Prompt(false));
          dispatch(taxSlice.actions.toggleShowInvalidLocalityPrompt(false));
        }}
        isTotalOrCity={taxState.showInvalidLocalityPrompt}
      />
      {(uploadW2Task || confirmW2Task || deleteW2Task) && <LineLoader />}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  taxState: state.tax,
  user: state.user.data,
});

export default connect(mapStateToProps)(FormsW2);
